import { EmployeeModel } from '@app/reducers/orm/employee/employee.model';
import type { AccessConstraint } from '@sb/shared/access';

import { type MenuDeps, type MenuNode, type MenuVisibilityContext, type NinjaAction } from './command-palette.model';

type Translator = (key: string) => string;

/** Whether the current user satisfies a node's access constraint. */
export type AccessPredicate = (constraint: AccessConstraint) => boolean;

export interface ResolveMenuOptions {
  nodes: MenuNode[];
  translate: Translator;
  employees: readonly EmployeeModel[];
  deps: MenuDeps;
  canAccess: AccessPredicate;
  visibility: MenuVisibilityContext;
}

/** Invariants shared across the recursive walk, kept off the per-call argument list. */
interface WalkContext {
  usedIds: Set<string>;
  translate: Translator;
  employees: readonly EmployeeModel[];
  deps: MenuDeps;
  canAccess: AccessPredicate;
  visibility: MenuVisibilityContext;
  out: NinjaAction[];
}

export function resolveMenu({
  nodes,
  translate,
  employees,
  deps,
  canAccess,
  visibility,
}: ResolveMenuOptions): NinjaAction[] {
  const context: WalkContext = {
    usedIds: new Set<string>(),
    translate,
    employees,
    deps,
    canAccess,
    visibility,
    out: [],
  };
  walkNodes(nodes, null, context);
  return context.out;
}

function walkNodes(nodes: MenuNode[], parentId: string | null, context: WalkContext): string[] {
  const childIds: string[] = [];

  for (const node of nodes) {
    if (!isNodeVisible(node, context)) {
      continue;
    }

    const title = context.translate(node.titleKey);
    const action = buildAction(node, title, parentId, context);
    context.usedIds.add(action.id);

    const staticChildIds = walkNodes(node.children ?? [], action.id, context);
    const dynamicChildren = resolveDynamicChildren(node, action.id, context);

    if (staticChildIds.length > 0 || dynamicChildren.length > 0) {
      const dynamicChildIds = dynamicChildren.map((dynamicAction) => {
        context.out.push(dynamicAction);
        return dynamicAction.id;
      });
      action.children = [...staticChildIds, ...dynamicChildIds];
    } else if (node.route || node.onSelect) {
      attachHandler(action, node, context.deps);
    } else {
      // A group whose children were all filtered out, with no action of its own:
      // drop it rather than leave a dead, unselectable entry in the palette.
      continue;
    }

    context.out.push(action);
    childIds.push(action.id);
  }

  return childIds;
}

// A node declares nothing, an `access` constraint, an `isVisible` predicate, or both, and
// every gate it does declare has to pass. Access is a direct reference to the destination's
// descriptor and `isVisible` a predicate it shares with its nav item (see `MenuNode`) —
// there is no URL derivation and no rule spelled out per menu.
function isNodeVisible(node: MenuNode, { canAccess, visibility }: WalkContext): boolean {
  return (
    (node.access === undefined || canAccess(node.access)) &&
    (node.isVisible === undefined || node.isVisible(visibility))
  );
}

function buildAction(node: MenuNode, title: string, parentId: string | null, context: WalkContext): NinjaAction {
  const action: NinjaAction = {
    id: pickUniqueId(title, parentId, context.usedIds),
    title,
  };

  if (parentId) {
    action.parent = parentId;
  }

  if (node.keywordKeys?.length) {
    action.keywords = node.keywordKeys.map(context.translate).join(' ');
  }

  return action;
}

function pickUniqueId(title: string, parentId: string | null, usedIds: Set<string>): string {
  if (!usedIds.has(title)) {
    return title;
  }

  if (parentId) {
    const qualified = `${parentId} / ${title}`;
    if (!usedIds.has(qualified)) {
      return qualified;
    }
  }

  let suffix = 2;
  const base = parentId ? `${parentId} / ${title}` : title;
  while (usedIds.has(`${base} (${suffix})`)) {
    suffix += 1;
  }
  return `${base} (${suffix})`;
}

function resolveDynamicChildren(node: MenuNode, parentId: string, context: WalkContext): NinjaAction[] {
  if (node.dynamicChildren !== 'employees') {
    return [];
  }

  return context.employees
    .filter((employee): employee is EmployeeModel & { id: string } => !!employee.id)
    .map((employee) => {
      const id = pickUniqueId(employee.name, parentId, context.usedIds);
      context.usedIds.add(id);
      return {
        id,
        title: employee.name,
        parent: parentId,
        handler: () => {
          context.deps.router.navigate(['employees', employee.id, 'overview']);
        },
      };
    });
}

function attachHandler(action: NinjaAction, node: MenuNode, deps: MenuDeps): void {
  if (node.route) {
    const route = node.route;
    action.handler = () => {
      deps.router.navigate(route);
    };
    return;
  }

  if (node.onSelect) {
    const onSelect = node.onSelect;
    action.handler = () => onSelect(deps);
  }
}
